import { DateTime } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";
import { AsyncStateManager } from "@tento-core/async-state-manager";
import { BarChartIcon, LonaIcons } from "@tento-ui/ui/icons";
import {
  type BindCellContext,
  type RenderPlugin,
  RowOptionsElement,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { type SheetRow } from "@tento-lona";
import { type JsonObject, type Option, RowId } from "@tento-lona/sheets";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { DataPluginCell } from "../alias/data/cell.ts";
import { DataStore } from "../alias/data/data-store.ts";
import { COLUMN_DATA_JSON_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import type { SheetLegendClass } from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { dateCellInColumn, dtrCellInColumn } from "./column-cell-overlap.ts";
import { DataJsonLegend, JsonHide } from "./column-data-json-legend.ts";

/** The renderer plugin that shows a data row's raw JSON cells. */
export class ColumnDataJsonRendererPlugin
  implements RenderPlugin.Renderer<DataPluginCell, DataJsonLegend>
{
  static readonly spec: PerTypeSpec = COLUMN_DATA_JSON_SPEC;

  readonly role = "renderer";
  readonly id: string = "column.data-json";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(BarChartIcon);

  #stateManager = new AsyncStateManager();

  constructor(
    public readonly makeRowOptions: RowOptionsElement.Factory,
    public readonly store: DataStore,
    public readonly patchRowAttributes: (
      rowId: RowId,
      attrs: JsonObject,
    ) => void,
  ) {
    this.legendFactory = DataJsonLegend.makeFactory(this);
  }

  // Hide state for the row's raw JSON cells — see `JsonHide` for the
  // persisted attribute shape and why it isn't `attributes.visible`.
  canHide(_row: SheetRow.Joined.Handle): boolean {
    return true;
  }

  isHidden(row: SheetRow.Joined.Handle): boolean {
    return JsonHide.isHiddenAttrs(row.attributes());
  }

  setHidden(row: SheetRow.Joined.Handle, hidden: boolean): void {
    this.patchRowAttributes(RowId.fromLocalId(row.id), JsonHide.attrsFor(hidden ? "hidden" : "visible"));
  }

  makeCell(): DataPluginCell {
    return DataPluginCell.make();
  }

  async bindCell(
    $cell: DataPluginCell,
    { sheet: rowList, row, sheetRow, column, handle, widthPx }: BindCellContext,
  ) {
    $cell.bindAnchor({
      handle,
      column,
      rowId: RowId.fromLocalId(sheetRow.id),
      localId: sheetRow.id,
    });
    $cell.bindLoading();

    // Row's raw JSON hidden via the legend eye — blank the cell, skip the
    // fetch. The legend (with the toggle back) stays visible.
    if (this.isHidden(row)) {
      $cell.style.width = widthPx + "px";
      $cell.bindJson([], []);
      return;
    }

    const parentRow = row.parent() ?? row;
    if (!parentRow) {
      $cell.bindJson([], []);
      return;
    }

    const col = column.discrete();
    const tz = col[1];
    const dr = col[0].toRange();
    const colDtr = new DateTime.Range(
      tz.toWallClock(dr.start.withTime()),
      tz.toWallClock(dr.end.withTime()),
    );
    const dataPromise = this.store.getAllInRange(rowList, parentRow, col, []);
    const stillValid = this.#stateManager.bind($cell, dataPromise);

    dataPromise
      .then(({ cells, customCells }) => {
        if (!stillValid(dataPromise)) return;
        $cell.style.width = widthPx + "px";
        cells = cells.filter((c) => dateCellInColumn(c.date.toRange(), dr));
        const cellsTz = customCells
          .map((c) => ({
            id: c.id,
            dtr: new DateTime.Range(tz.toTz(c.dtr.start), tz.toTz(c.dtr.end)),
            value: c.value,
            style: c.style,
          }))
          .filter((c) => dtrCellInColumn(c.dtr, colDtr));
        cellsTz.sort((a, b) => a.dtr.start.mse - b.dtr.end.mse);
        cells.sort((a, b) => a.date.start.dse - b.date.start.dse);
        $cell.bindJson(cells, cellsTz);
      })
      .catch((e) => {
        if (!stillValid(dataPromise)) return;
        Tracing.err("ui")("ColumnDataJsonRendererPlugin: cell data load failed", String(e));
        $cell.bindJson([], []);
      });
  }

  // Label row + eye toggle for the raw JSON cells (see ctor — the factory
  // closes over this plugin instance for isHidden/setHidden).
  readonly legendFactory: SheetLegendClass<DataJsonLegend>;

  loadData = null;

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }

  onBindRow(_handle: SheetDelegate, _row: SheetRow.Joined.Handle): void {}
  onResize($cell: DataPluginCell, { widthPx }: BindCellContext): void {
    $cell.style.width = widthPx + "px";
    $cell.refreshExpand();
  }
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}
}
